import { useEffect, useMemo, useState } from 'react'
import { Compteur } from '../Compteur'
import {
  affecterAutomatiquement,
  affecterLivreur,
  chargerConfiguration,
  listerLivreurs,
  suivrePositions,
} from '../../api/dispatch'
import { chargerParametresTVA, decomposer, TVA_INACTIVE, type ParametresTVA } from '../../api/compta'
import {
  STATUTS_ACTIFS,
  badgeStatut,
  libelleStatut,
  type Commande,
  type Configuration,
  type Livreur,
} from '../../api/types'
import { depuis, fcfa } from '../Format'
import { BarreMeteo } from '../BarreMeteo'
import { CarteLive } from '../CarteLive'
import { FicheCommande } from '../FicheCommande'
import { ChoixLivreur } from '../ChoixLivreur'
import { peutAssigner } from '../../api/livreursAssignables'
import type { Casquette } from '../../api/discussions'

/**
 * Live — TRANSPOSITION du gabarit d'origine.
 *
 * Le portage précédent n'avait ni barre météo, ni carte, ni légende, et ses
 * quatre tuiles n'étaient pas celles de l'original : il comptait des
 * « positions reçues », une donnée technique, là où le dispatch a besoin du
 * délai moyen, de l'encaissé et de notre CA.
 */
export function EcranLive({
  commandes,
  surRecharger,
  surContacter,
  surVoirDiscussion,
}: {
  commandes: Commande[]
  surRecharger: () => Promise<void>
  surContacter: (pairId: string, casquette: Casquette) => void
  surVoirDiscussion: (commandeId: string) => void
}) {
  const [livreurs, setLivreurs] = useState<Livreur[]>([])
  const [config, setConfig] = useState<Configuration | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  /* La commande pour laquelle on choisit un livreur (liste des livreurs actifs et disponibles). */
  const [aAssigner, setAAssigner] = useState<Commande | null>(null)
  const [legende, setLegende] = useState(false)
  const [fiche, setFiche] = useState<Commande | null>(null)
  /* Le « revenu » de la tuile passe par la MÊME fonction que la comptabilité.
     Sans le taux, il s'afficherait toutes taxes comprises pendant que l'onglet
     Comptabilité l'affiche hors taxes : deux chiffres différents pour le même
     mot, dans la même console. */
  const [tva, setTva] = useState<ParametresTVA>(TVA_INACTIVE)

  useEffect(() => {
    void listerLivreurs().then(setLivreurs).catch(() => setErreur('Livreurs indisponibles.'))
    void chargerConfiguration().then(setConfig).catch(() => undefined)
    void chargerParametresTVA().then(setTva).catch(() => undefined)
    /* Chaque relevé est une ligne complète : l'événement EST la donnée, on ne
       relit rien. Le dernier point est reporté sur la fiche du livreur pour que
       la CARTE suive le déplacement — sans ça les marqueurs restaient figés à
       la position du chargement. */
    return suivrePositions((p) =>
      setLivreurs((liste) =>
        liste.map((l) => (l.id === p.livreurId ? { ...l, lat: p.lat, lng: p.lng } : l)),
      ),
    )
  }, [])

  const actives = commandes.filter((c) => STATUTS_ACTIFS.includes(c.statut))
  const enLigne = livreurs.filter((l) => l.etat !== 'offline').length

  const jour = useMemo(() => {
    /* Journée de BAMAKO (UTC+0, sans heure d'été), pas celle du navigateur :
       un opérateur en déplacement verrait sinon la journée d'un autre fuseau. */
    const aujourdhui = new Date().toISOString().slice(0, 10)
    const livrees = commandes.filter(
      (c) => c.statut === 'delivered' && (c.livreeLe ?? '').slice(0, 10) === aujourdhui,
    )
    /* Durée RÉELLE : du retrait à la livraison, avec repli sur la création si
       l'horodatage de retrait manque. Le garde-fou écarte les aberrations —
       une course de 14 heures est un horodatage cassé, pas une performance. */
    const durees = livrees
      .map((c) => {
        if (!c.livreeLe) return null
        const debut = c.retireeLe ?? c.creeeLe
        if (!debut) return null
        const min = Math.round((new Date(c.livreeLe).getTime() - new Date(debut).getTime()) / 60000)
        return min > 0 && min < 600 ? min : null
      })
      .filter((v): v is number => v != null)
    /* DEUX montants, parce qu'ils ne veulent pas dire la même chose : l'ENCAISSÉ
       est ce que les clients ont payé et qui transite par nos comptes mobile
       money — part marchand et part livreur comprises, que nous leur devons. Le
       REVENU est ce qui nous reste. En modèle agent, les confondre gonfle le
       chiffre d'affaires d'un facteur cinq. */
    return {
      livrees: livrees.length,
      moyenne: durees.length ? Math.round(durees.reduce((s, v) => s + v, 0) / durees.length) : 0,
      encaisse: livrees.reduce((s, c) => s + decomposer(c, tva).encaisse, 0),
      revenu: livrees.reduce((s, c) => s + decomposer(c, tva).revenu, 0),
    }
  }, [commandes, tva])

  /* Le choix d'un livreur précis, ou « le premier disponible » (option rapide).
     Les erreurs du serveur (déjà pris, pas actif…) remontent à la modale, qui les
     affiche en phrase ; on ne ferme qu'en cas de succès. */
  async function choisir(commandeId: string, livreurId: string) {
    await affecterLivreur(commandeId, livreurId)
    setAAssigner(null)
    setErreur(null)
    await surRecharger()
  }

  async function premierDisponible(commande: Commande) {
    const origine =
      commande.marchandLat !== null && commande.marchandLng !== null
        ? { lat: commande.marchandLat, lng: commande.marchandLng }
        : null
    const r = await affecterAutomatiquement(commande.id, origine, commande.clientId)
    if (r === 'aucun_livreur') throw new Error('Aucun livreur actif et disponible pour le moment.')
    setAAssigner(null)
    setErreur(null)
    await surRecharger()
  }

  return (
    <>
      <BarreMeteo
        baremes={config?.bonusMeteo ?? []}
        meteoActuelle={config?.meteoActuelle ?? null}
        surBascule={async () => {
          setConfig(await chargerConfiguration())
        }}
      />

      <div className="stats">
        <div className="stat">
          <div className="label">Commandes actives</div>
          <div className="val"><Compteur valeur={String(actives.length)} /></div>
        </div>
        <div className="stat">
          <div className="label">Livreurs en ligne</div>
          <div className="val"><Compteur valeur={String(enLigne)} /></div>
        </div>
        <div className="stat">
          <div className="label">Livrées aujourd'hui</div>
          <div className="val"><Compteur valeur={String(jour.livrees)} /></div>
        </div>
        <div className="stat">
          <div className="label">Temps moyen livr.</div>
          <div className="val">{jour.moyenne} min</div>
        </div>
        <div
          className="stat"
          title="Total payé par les clients — inclut la part marchand et la part livreur, que nous leur devons"
        >
          <div className="label">Encaissé aujourd'hui</div>
          <div className="val"><Compteur valeur={fcfa(jour.encaisse)} /></div>
        </div>
        <div
          className="stat"
          title="Commission + livraison + frais accessoires — notre chiffre d'affaires au sens de la comptabilité"
        >
          <div className="label">Notre CA aujourd'hui</div>
          <div className="val" style={{ color: 'var(--green)' }}>
            <Compteur valeur={fcfa(jour.revenu)} />
          </div>
        </div>
      </div>

      <CarteLive commandes={commandes} livreurs={livreurs} />

      {erreur && <p style={{ color: 'var(--danger)', padding: '6px 2px', fontSize: 13 }}>{erreur}</p>}

      <div className="card">
        <div className="card-header">Commandes actives</div>
        <div className="card-body">
          <table>
            <thead>
              <tr>
                <th>Ref</th>
                <th>Marchand</th>
                <th>Client</th>
                <th>Livreur</th>
                <th>Statut</th>
                <th>Temps</th>
                <th>Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {actives.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--n400)', padding: 18 }}>
                    Aucune commande active.
                  </td>
                </tr>
              )}
              {actives.map((c) => (
                <tr key={c.id}>
                  <td>
                    {/* CLIQUABLE : c'est par la référence qu'on ouvre la fiche.
                        En texte simple, un client au téléphone donnait un
                        numéro qui ne menait nulle part. */}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        setFiche(c)
                      }}
                    >
                      {c.ref ?? c.id.slice(0, 6).toUpperCase()}
                    </a>
                  </td>
                  <td>{c.marchandNom}</td>
                  <td>{c.clientNom}</td>
                  <td>
                    {c.livreurId ? (
                      <a
                        href="#"
                        title="Contacter ce livreur"
                        onClick={(e) => {
                          e.preventDefault()
                          surContacter(c.livreurId!, 'livreur')
                        }}
                      >
                        {c.livreurNom}
                      </a>
                    ) : (
                      /* On n'affecte que si personne n'est dessus : réaffecter
                         en cours de route ferait partir deux personnes chercher
                         le même plat. Et pas avant que le marchand ait accepté :
                         le serveur refuserait (transition non permise). */
                      peutAssigner(c.statut, c.livreurId) ? (
                        <button className="btn btn-accent btn-sm" onClick={() => setAAssigner(c)}>
                          Assigner
                        </button>
                      ) : (
                        <span
                          style={{ color: 'var(--n400)', fontSize: 12 }}
                          title="Un livreur peut être assigné dès que le marchand a accepté la commande."
                        >
                          après acceptation
                        </span>
                      )
                    )}
                  </td>
                  <td>
                    <span className={`badge ${badgeStatut(c.statut)}`}>{libelleStatut(c.statut)}</span>
                  </td>
                  <td>{depuis(c.creeeLe)}</td>
                  <td>{fcfa(c.total)}</td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => setFiche(c)}>
                      Detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FicheCommande
        commande={fiche}
        surFermer={() => setFiche(null)}
        surVoirDiscussion={surVoirDiscussion}
        surAnnulee={async () => {
          setErreur(null)
          await surRecharger()
        }}
      />

      <ChoixLivreur
        commande={aAssigner}
        surFermer={() => setAAssigner(null)}
        surChoisi={(livreurId) => choisir(aAssigner!.id, livreurId)}
        surPremier={() => premierDisponible(aAssigner!)}
      />

      {/* LÉGENDE — repliable, fermée par défaut. Ce n'est pas de la
          documentation décorative : elle dit QUI agit à chaque étape. Sans
          elle, le dispatch appelle le marchand pour une action qui revient au
          livreur, et réciproquement. */}
      <div className="card legend-card">
        <div
          className="card-header"
          style={{ cursor: 'pointer' }}
          onClick={() => setLegende((o) => !o)}
        >
          Parcours d'une commande
          <span style={{ color: 'var(--n400)', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            cliquer pour ouvrir / fermer
          </span>
        </div>
        <div className="legend-content" style={{ display: legende ? 'block' : 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Statut</th>
                <th>Signification</th>
                <th>Qui agit ?</th>
                <th>Suivant</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span className="badge badge-gray">pending</span>
                </td>
                <td>Commande creee, en attente de paiement</td>
                <td>Client</td>
                <td>paid</td>
              </tr>
              <tr>
                <td>
                  <span className="badge badge-blue">paid</span>
                </td>
                <td>Paiement confirme</td>
                <td>Wave / Orange Money</td>
                <td>accepted</td>
              </tr>
              <tr>
                <td>
                  <span className="badge badge-blue">accepted</span>
                </td>
                <td>Marchand a accepte la commande</td>
                <td>Marchand</td>
                <td>preparing</td>
              </tr>
              <tr>
                <td>
                  <span className="badge badge-orange">preparing</span>
                </td>
                <td>
                  Commande en preparation. <b>2 axes independants a partir d'ici</b> : le marchand
                  prepare (→ ready) ET un livreur peut etre affecte (→ rider_assigned), dans
                  n'importe quel ordre.
                </td>
                <td>Marchand / Dispatch</td>
                <td>ready ou rider_assigned</td>
              </tr>
              <tr>
                <td>
                  <span className="badge badge-yellow">ready</span>
                </td>
                <td>
                  Prete (le marchand clique quand c'est pret). Peut arriver AVANT qu'un livreur soit
                  affecte.
                </td>
                <td>Marchand</td>
                <td>rider_assigned / picked_up</td>
              </tr>
              <tr>
                <td>
                  <span className="badge badge-blue">rider_assigned</span>
                </td>
                <td>
                  Livreur affecte (par le dispatch ou auto-affecte). Peut arriver PENDANT la prepa ;
                  le marchand pourra encore marquer « pret » ensuite.
                </td>
                <td>Dispatch / Livreur</td>
                <td>picked_up</td>
              </tr>
              <tr>
                <td
                  colSpan={4}
                  style={{ background: 'var(--accent-light)', fontSize: 12, color: 'var(--n700)' }}
                >
                  <b>Retrait (picked_up)</b> = uniquement quand les DEUX sont vrais : commande{' '}
                  <b>prete</b> (marchand) ET <b>livreur affecte</b>.
                </td>
              </tr>
              <tr>
                <td>
                  <span className="badge badge-orange">picked_up</span>
                </td>
                <td>Livreur a recupere la commande</td>
                <td>Livreur</td>
                <td>delivering</td>
              </tr>
              <tr>
                <td>
                  <span className="badge badge-orange">delivering</span>
                </td>
                <td>En cours de livraison</td>
                <td>Livreur</td>
                <td>delivered</td>
              </tr>
              <tr>
                <td>
                  <span className="badge badge-green">delivered</span>
                </td>
                <td>Livree au client</td>
                <td>&mdash;</td>
                <td>&mdash;</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
